import { NOT_FOUND } from 'constants/not-found';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import {
  ContentHostSelector,
  EXP_COMMERCIAL_TENANT_ID,
  EXP_IN_SERVICE_TENANT_ID,
  getTenantById,
  TenantId,
} from '@tenants';
import { getContentPageUrlForTenant, getBlogContentPageUrlForTenant } from '@tenants/utils/hosts';

import type { GetServerSidePropsResult } from 'next';
import { ContentPageProps } from 'types/content-pages';
import { HeadDataType } from 'components/head-data';
import { SiteLanguage } from 'types/locales';

export function isContentPageProps(props: object): props is ContentPageProps {
  return 'siteContent' in props;
}

export async function handleContentPagesFetch(url: string, headData?: HeadDataType, locale?: string, tenantId?: TenantId): Promise<GetServerSidePropsResult<ContentPageProps>> {
  const tenant = getTenantById(tenantId);
  let contentPageUrl = url;
  if (locale && locale !== tenant.localization.defaultLanguage && tenant.features.HasMultilingualContentPages) {
    // (Temporarily) use the same locale for fr-CA as fr
    const localeToUse = locale === SiteLanguage.FrenchCA ? SiteLanguage.French : locale;
    contentPageUrl = `${url}-${localeToUse}`;
  }

  try {
    const response = await fetchWithRetry(contentPageUrl);
    if (!response.ok) {
      console.error(`Failed to fetch content from ${contentPageUrl} with status ${response.status}`);
      return NOT_FOUND;
    }

    const content = await response.text();
    if (!content || content.length === 0) {
      console.error(`Empty content received from ${contentPageUrl}`);
      return NOT_FOUND;
    }

    const siteContent = content.concat('<!-- End of WordPress Content --><div></div>');

    return {
      props: {
        siteContent,
        ...(headData && { headData }),
      },
    };
  } catch (error: unknown) {
    console.error(`Error fetching content from ${url}: ${error}`);
    return NOT_FOUND;
  }
}

export async function handleContentPageServerSideProps(tenantId: TenantId, expectedTenants: TenantId[], url: string, filter: ContentHostSelector = 'ALL', headData?: HeadDataType, locale?: string) {
  if (!tenantId || (expectedTenants.length > 0 && !expectedTenants.some(tenant => tenant === tenantId))) {
    return NOT_FOUND;
  }

  const host = getContentPageUrlForTenant(tenantId, filter);
  return await handleContentPagesFetch(`${host}${url}`, headData, locale, tenantId);
}

export async function handleBlogContentPageServerSideProps(tenantId: TenantId, expectedTenants: TenantId[], url: string) {
  if (!tenantId || (expectedTenants.length > 0 && !expectedTenants.some(tenant => tenant === tenantId))) {
    return NOT_FOUND;
  }

  const host = getBlogContentPageUrlForTenant(tenantId);
  return await handleContentPagesFetch(`${host}${url}`);
}

export async function handleInserviceLifeBlogServerSideProps(tenantId: TenantId, url: string) {
  return await handleContentPageServerSideProps(tenantId, [EXP_IN_SERVICE_TENANT_ID], url, 'ALL');
}

export async function handleCommercialLifeBlogServerSideProps(tenantId: TenantId, url: string) {
  return await handleContentPageServerSideProps(tenantId, [EXP_COMMERCIAL_TENANT_ID], url, 'ALL');
}
